import React, { useState } from 'react';
import { Box, Container, Typography, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { Info, Lightbulb, Share, Security, ContactMail } from '@mui/icons-material';

const policySections = [
  {
    id: "collection",
    number: "1",
    icon: <Info />,
    title: "Information We Collect",
    content: "We collect information you provide directly to us, such as when you create or modify your account, request on-demand services, contact customer support, or otherwise communicate with us. This information may include: name, email, phone number, postal address, profile picture, payment method, items requested (for delivery services), delivery notes, and other information you choose to provide."
  },
  {
    id: "usage",
    number: "2",
    icon: <Lightbulb />,
    title: "How We Use Information",
    content: "We may use the information we collect about you to: Provide, maintain, and improve our Services, including, for example, to facilitate payments, send receipts, provide products and services you request (and send related information), develop new features, provide customer support to Users, develop safety features, authenticate users, and send product updates and administrative messages."
  },
  {
    id: "sharing",
    number: "3",
    icon: <Share />,
    title: "Sharing of Information",
    content: "We may share the information we collect about you as described in this Statement or as described at the time of collection or sharing, including as follows: With third parties to provide you a service you requested through a partnership or promotional offering made by a third party or us; With the general public if you submit content in a public forum, such as blog comments, social media posts, or other features of our Services that are viewable by the general public."
  },
  {
    id: "security",
    number: "4",
    icon: <Security />,
    title: "Data Security",
    content: "We take reasonable measures to help protect information about you from loss, theft, misuse and unauthorized access, disclosure, alteration and destruction. However, no data transmission over the Internet can be guaranteed to be 100% secure. As a result, while we strive to protect your personal information, we cannot ensure or warrant the security of any information you transmit to us."
  },
  {
    id: "contact",
    number: "5",
    icon: <ContactMail />,
    title: "Contact Us",
    content: "If you have any questions about this Privacy Statement, please contact us at arinfotechsolutions38@gmail.com or call +91 91132 99131."
  }
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(policySections[0].id);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -115;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F1F5F9', pb: 10 }}>
      {/* Banner Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 100%)',
          color: '#FFF',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center'
        }}
      >
        {/* Animated Background Elements */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 150, repeat: Infinity, ease: "linear" }}
          style={{
            position: 'absolute',
            top: '-50%',
            left: '-20%',
            width: '100%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, rgba(0,0,0,0) 70%)',
            pointerEvents: 'none'
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
              Privacy Policy
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
              Your privacy is critically important to us. Learn how we handle your data.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 3, md: 5 }, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {/* Table of Contents (Sidebar) */}
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 10px 30px -10px rgba(0,0,0,0.05)',
                position: { md: 'sticky' },
                top: { md: 100 },
                backgroundColor: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1E293B' }}>
                Table of Contents
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {policySections.map((section) => (
                  <Box
                    key={`toc-${section.id}`}
                    onClick={() => scrollToSection(section.id)}
                    sx={{
                      cursor: 'pointer',
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      transition: 'all 0.2s',
                      backgroundColor: activeSection === section.id ? 'rgba(30, 58, 138, 0.08)' : 'transparent',
                      color: activeSection === section.id ? '#1E3A8A' : '#64748B',
                      borderLeft: activeSection === section.id ? '3px solid #1E3A8A' : '3px solid transparent',
                      fontWeight: activeSection === section.id ? 600 : 400,
                      '&:hover': {
                        backgroundColor: 'rgba(30, 58, 138, 0.04)',
                        color: '#1E3A8A'
                      }
                    }}
                  >
                    <Typography variant="body2">{section.number}. {section.title}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Main Content Areas */}
          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {policySections.map((section, index) => (
                <motion.div
                  key={`content-${section.id}`}
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                    <Paper
                      id={section.id}
                      elevation={2}
                      sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        scrollMarginTop: { xs: '105px', md: '120px' },
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(0,0,0,0.05)',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box sx={{
                        backgroundColor: '#1E3A8A',
                        color: '#FFF',
                        width: 44,
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10%',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        flexShrink: 0
                      }}>
                        {section.number}
                      </Box>
                      <Box sx={{ color: '#1E3A8A', display: 'flex', opacity: 0.8 }}>
                        {section.icon}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {section.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.8 }}>
                      {section.content}
                    </Typography>
                  </Paper>
                </motion.div>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
